import type { JSX } from "react";
import { Link } from "react-router";
import { toLocalDate } from "../../domain/time";
import type { LocalEvent } from "../../offline/model";
import { useOffline } from "../../offline/OfflineContext";
import { usePurchasePaths } from "../../platform/PlatformContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import { Alert } from "../../shared/ui/Alert";
import { describeErrorCode } from "../../shared/ui/describeError";
import { formatDateTime } from "../../shared/ui/format";
import styles from "../../shared/ui/ui.module.css";
import clockStyles from "./ClockPage.module.css";

/** Properties of {@link ConflictList}. */
export interface ConflictListProps {
  /** Rejected local events. */
  readonly conflicts: readonly LocalEvent[];
}

/**
 * Lists events the server rejected (spec 8.1.4: "Konflikt klären") with the way to a correction
 * of that day. The reason is the translated message of the error code, never the server text
 * (UX-107). The items are static callouts without a live region: the synchronization status
 * already announces the number of conflicts (UX-106).
 *
 * @param props - Component properties.
 * @returns The list or nothing.
 */
export function ConflictList(props: ConflictListProps): JSX.Element | null {
  const { t, languageTag } = useI18n();
  const purchasePaths = usePurchasePaths();
  const { store } = useOffline();

  if (props.conflicts.length === 0) {
    return null;
  }

  const section = (
    <section aria-labelledby="conflicts-heading">
      <h2 id="conflicts-heading">{t("clock.conflicts")}</h2>
      <ul className={clockStyles["list"]}>
        {props.conflicts.map((event) => {
          const date = toLocalDate(event.occurredAt, event.timeZoneId);
          const item = (
            <li key={event.id}>
              <Alert tone="danger" live="off">
                <p>
                  {t("clock.conflict", {
                    action: t(`clock.action.${event.kind}`),
                    time: formatDateTime(event.occurredAt, languageTag, event.timeZoneId),
                    reason: describeErrorCode(event.error?.code ?? null, t, purchasePaths),
                  })}
                </p>
                <div className={styles["row"]}>
                  <Link
                    className={styles["button"]}
                    to={`/corrections?date=${date}#new-correction`}
                  >
                    {t("clock.conflictCorrection")}
                  </Link>
                  <button
                    type="button"
                    className={styles["button"]}
                    onClick={() => {
                      void store.dismissRejected(event.id);
                    }}
                  >
                    {t("clock.conflictDismiss")}
                  </button>
                </div>
              </Alert>
            </li>
          );

          return item;
        })}
      </ul>
    </section>
  );

  return section;
}
